import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import queryString from "query-string";
import {
    getAccessToken,
    getRefreshToken,
    setTokens,
    clearAuth,
    isTokenExpired
} from "common/auth";
import { getApiUrl } from "./getApiUrl";

export const RequestMethod = {
    GET: 'GET',
    POST: 'POST',
    DELETE: 'DELETE',
    PATCH: 'PATCH',
    PUT: 'PUT'
} as const;

type RequestMethod = typeof RequestMethod[keyof typeof RequestMethod];

export type RequestProps = {
    method?: RequestMethod;
    url: string;
    query?: { [key: string]: string | number };
    headers?: any;
    params?: any;
};

// ============================================
// Axios Instance 생성
// ============================================
export const instance = axios.create({
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// ============================================
// Request Interceptor - 토큰 자동 주입
// ============================================
instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const accessToken = getAccessToken();

        if (accessToken) {
            // 게이트웨이의 TokenExtractor가 'Authorization: Bearer <token>' 표준 형식을 expect
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// ============================================
// Response Interceptor - 토큰 만료 처리 (lazy refresh)
// 401 받으면 그때 refresh 시도 → retry. webview 화면이 짧게 떠있으니 주기적 polling은 불필요.
// ============================================
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
    refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
};

const refreshAccessToken = async (): Promise<string | null> => {
    const refreshToken = getRefreshToken();

    if (!refreshToken || isTokenExpired(refreshToken)) {
        console.warn('⚠️ Refresh token is missing or expired');
        return null;
    }

    try {
        console.log('🔄 Attempting to refresh access token...');

        const response = await axios.post(getApiUrl('refresh'), {
            refreshToken: refreshToken
        });

        const newAccessToken = response.headers['accesstoken'] || response.data?.accessToken;
        const newRefreshToken = response.headers['refreshtoken'] || response.data?.refreshToken;

        if (newAccessToken) {
            setTokens(newAccessToken, newRefreshToken || refreshToken);
            console.log('✅ Token refreshed successfully');
            return newAccessToken;
        }
    } catch (error) {
        console.error('❌ Token refresh failed:', error);
    }

    return null;
};

instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // 401 Unauthorized 처리
        if (error.response?.status === 401) {
            // 이미 재시도한 요청이 아닌 경우
            if (!originalRequest._retry) {
                originalRequest._retry = true;

                if (!isRefreshing) {
                    isRefreshing = true;

                    const newToken = await refreshAccessToken();

                    isRefreshing = false;

                    if (newToken) {
                        onTokenRefreshed(newToken);
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return instance(originalRequest);
                    } else {
                        // 리프레시 실패 시 로그아웃
                        console.warn('❌ Token refresh failed, clearing auth...');
                        clearAuth();
                        // 로그인 페이지로 리다이렉트는 앱에서 처리
                        return Promise.reject(error);
                    }
                } else {
                    // 이미 리프레시 중이면 대기
                    return new Promise((resolve) => {
                        subscribeTokenRefresh((token: string) => {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(instance(originalRequest));
                        });
                    });
                }
            }
        }

        return Promise.reject(error);
    }
);

// ============================================
// Query Parsing
// ============================================
const onParseQuery = (query?: { [key: string]: any } | string) => {
    if (!query) return '';
    if (typeof query === 'string') return `?${query}`;
    return '?' + queryString.stringify(query);
};

// ============================================
// Request Function
// ============================================
export const onRequest = async (props: RequestProps): Promise<AxiosResponse> => {
    try {
        const response = await instance({
            method: props.method,
            url: props.url + onParseQuery(props.query),
            data: props.params,
            headers: props.headers,
        });
        return response;
    } catch (error: any) {
        return error.response || error;
    }
};
